import React, { useState, useEffect } from 'react';
import { 
    Box, Typography, Paper, TextField, MenuItem, 
    Button, Stack, Alert, CircularProgress, Divider, Grid, Chip 
} from '@mui/material'; 
import { AccessTime, History, Send, Warning } from '@mui/icons-material';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

const SuddenRequestForm = () => {
    const [loading, setLoading] = useState(false);
    const [history, setHistory] = useState([]);
    const [formData, setFormData] = useState({
        requestType: 'LATE_PERMISSION',
        requestDate: format(new Date(), 'yyyy-MM-dd'),
        requestHour: 1,
        reason: ''
    });

    // Fetch employee's own history
    const fetchMyRequests = async () => {
        try {
            // FIXED PATH: Matches @GetMapping("/me") in your Controller
            const res = await api.get('/sudden-requests/me');
            setHistory(res.data || []);
        } catch (err) { 
            console.error("History fetch error:", err); 
        }
    };

    useEffect(() => { 
        fetchMyRequests(); 
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.reason || formData.reason.length < 5) {
            return toast.error("Please provide a valid reason (min 5 characters)");
        }
        
        setLoading(true);
        try {
            // FIXED PATH: Matches @PostMapping in SuddenRequestController.java
            // Maps to SuddenRequestCreateDto in backend
            await api.post('/sudden-requests', formData);
            toast.success("Request submitted successfully");
            setFormData({ ...formData, reason: '' });
            fetchMyRequests();
        } catch (err) { 
            toast.error(err.response?.data?.message || "Failed to submit"); 
        } finally { 
            setLoading(false); 
        }
    };

    // 11:00 AM Rule Detection logic for UI warning
    const isLateSubmission = new Date().getHours() >= 11;

    return (
        <Box sx={{ p: 4, maxWidth: 1100, mx: 'auto' }}>
            <Grid container spacing={4}>
                {/* Form Section */}
                <Grid item xs={12} md={7}>
                    <Paper elevation={0} sx={{ p: 4, border: '1px solid #e2e8f0', borderRadius: 4 }}>
                        <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                            <AccessTime color="primary" />
                            <Typography variant="h5" fontWeight="900">New Sudden Request</Typography>
                        </Stack>
                        
                        <Divider sx={{ mb: 3 }} />

                        {/* 11:00 AM Warning Alert */}
                        {isLateSubmission && (
                            <Alert severity="warning" icon={<Warning />} sx={{ mb: 3 }}>
                                <strong>Notice:</strong> Requests after 11:00 AM trigger a half-day deduction unless approved as "Paid" by your manager.
                            </Alert>
                        )}

                        <form onSubmit={handleSubmit}>
                            <Stack spacing={3}>
                                <TextField 
                                    select 
                                    label="Request Type" 
                                    fullWidth 
                                    value={formData.requestType} 
                                    onChange={(e) => setFormData({...formData, requestType: e.target.value})}
                                >
                                    <MenuItem value="LATE_PERMISSION">Late Permission (Short Leave)</MenuItem>
                                    <MenuItem value="EIGHT_HOUR_LEAVE">Sudden Leave (Full Day)</MenuItem>
                                </TextField>

                                <TextField 
                                    type="date" 
                                    label="Incident Date"
                                    fullWidth 
                                    InputLabelProps={{ shrink: true }}
                                    value={formData.requestDate} 
                                    onChange={(e) => setFormData({...formData, requestDate: e.target.value})} 
                                />

                                {formData.requestType === 'LATE_PERMISSION' && (
                                    <TextField 
                                        type="number" 
                                        label="Duration (Hours)" 
                                        fullWidth 
                                        value={formData.requestHour} 
                                        onChange={(e) => setFormData({...formData, requestHour: e.target.value})} 
                                    />
                                )}

                                <TextField 
                                    label="Reason" 
                                    multiline 
                                    rows={4} 
                                    fullWidth 
                                    placeholder="Explain the emergency briefly..."
                                    value={formData.reason} 
                                    onChange={(e) => setFormData({...formData, reason: e.target.value})} 
                                />

                                <Button 
                                    variant="contained" 
                                    type="submit" 
                                    size="large"
                                    disabled={loading} 
                                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Send />}
                                    sx={{ py: 1.5, fontWeight: 'bold' }}
                                >
                                    {loading ? 'Submitting...' : 'Send Request'}
                                </Button>
                            </Stack>
                        </form>
                    </Paper>
                </Grid>

                {/* History Section */}
                <Grid item xs={12} md={5}>
                    <Paper elevation={0} sx={{ p: 3, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 4 }}>
                        <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                            <History color="action" />
                            <Typography variant="h6" fontWeight="bold">My Sudden Requests</Typography>
                        </Stack>
                        <Divider sx={{ mb: 2 }} />
                        
                        {history.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">No recent history.</Typography>
                        ) : (
                            <Stack spacing={2}>
                                {history.map(req => (
                                    <Paper key={req.id} sx={{ p: 2, borderRadius: 2, border: '1px solid #eef2f6' }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                            <Typography variant="caption" fontWeight="bold">{req.requestDate}</Typography>
                                            <Chip 
                                                label={req.status} 
                                                size="small" 
                                                sx={{ 
                                                    height: 20, 
                                                    fontSize: '0.65rem',
                                                    fontWeight: 'bold',
                                                    bgcolor: req.status === 'APPROVED' ? '#dcfce7' : '#fee2e2',
                                                    color: req.status === 'APPROVED' ? '#166534' : '#991b1b'
                                                }}
                                            />
                                        </Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ 
                                            display: '-webkit-box', 
                                            WebkitLineClamp: 2, 
                                            WebkitBoxOrient: 'vertical', 
                                            overflow: 'hidden' 
                                        }}>
                                            {req.reason}
                                        </Typography>
                                    </Paper>
                                ))}
                            </Stack>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default SuddenRequestForm;